import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { TipoArquivo } from "@workflow/types";

import { fileTypeConfig } from "./file-type-config";
import { genInputId } from "./format";
import { deriveAccept, validateFile } from "./validation";

interface UseFileUploadParams {
  type: TipoArquivo;
  onFileChange: (file: File | null) => void;
  defaultFile: File | null;
  disabled?: boolean;
  uniqueId?: string;
  maxFileSizeMB: number;
  accept?: string;
  /** Ativa o paste (Ctrl+V) sem exigir hover — para telas com um único campo de upload (ex.: modal dedicado). */
  pasteSempreAtivo?: boolean;
}

/**
 * Encapsula todo o estado e os handlers do upload (seleção, validação, drag &
 * drop e remoção). As variantes visuais (`lite`/`default`) são puramente de
 * apresentação e consomem o retorno deste hook.
 */
export function useFileUpload({
  type,
  onFileChange,
  defaultFile,
  disabled,
  uniqueId,
  maxFileSizeMB,
  accept,
  pasteSempreAtivo,
}: UseFileUploadParams) {
  const palette = fileTypeConfig[type];
  const [validationError, setValidationError] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(defaultFile);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const inputId = useMemo(() => genInputId(type, uniqueId), [type, uniqueId]);
  const derivedAccept = useMemo(() => deriveAccept(type, accept), [type, accept]);

  function processFile(fileCandidate: File | null) {
    if (!fileCandidate) return;
    const result = validateFile(fileCandidate, type, maxFileSizeMB);
    if (result.ok === false) {
      toast.error(result.message);
      setValidationError(result.message);
      return;
    }
    setValidationError("");
    setSelectedFile(fileCandidate);
    onFileChange(fileCandidate);
  }

  const processFileRef = useRef(processFile);
  processFileRef.current = processFile;

  /**
   * Paste (Ctrl+V) é ativado por hover, não por foco — clicar na área já abre
   * o diálogo do SO, então exigir clique-para-focar antes do paste conflitaria
   * com esse fluxo. O listener é global (`window`) só enquanto o mouse está
   * sobre a área, e some ao sair. Quando `pasteSempreAtivo` (telas com um único
   * campo de upload, ex. modal dedicado), o listener fica ativo o tempo todo —
   * não há outro campo que disputaria o paste.
   */
  useEffect(() => {
    if (!(isHovering || pasteSempreAtivo) || disabled) return;

    function handlePaste(e: ClipboardEvent) {
      const pastedFile = Array.from(e.clipboardData?.items ?? [])
        .find((item) => item.kind === "file")
        ?.getAsFile();
      if (pastedFile) {
        e.preventDefault();
        processFileRef.current(pastedFile);
      }
    }

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [isHovering, pasteSempreAtivo, disabled]);

  const hoverHandlers = {
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
  };

  const triggerFileInput = () => document.getElementById(inputId)?.click();

  function removeFile(e: React.MouseEvent) {
    e.stopPropagation();
    setSelectedFile(null);
    onFileChange(null);
    setValidationError("");
  }

  const dragHandlers = {
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    onDragLeave: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
    },
    onDrop: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFile(e.dataTransfer.files[0]);
        e.dataTransfer.clearData();
      }
    },
  };

  const inputProps = {
    type: "file" as const,
    className: "hidden",
    id: inputId,
    accept: derivedAccept,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      processFile(e.target.files?.[0] ?? null);
      e.target.value = "";
    },
    disabled,
  };

  return {
    palette,
    selectedFile,
    isDragging,
    validationError,
    inputProps,
    dragHandlers,
    hoverHandlers,
    triggerFileInput,
    removeFile,
  };
}

export type FileUploadState = ReturnType<typeof useFileUpload>;
